import { useSelector } from "react-redux";
import { useEffect, useRef } from "react";

// Subscribes to the global ui slice (loading / success / error / info from
// TRADE-901) and fires component-level callbacks when each flag transitions.
//
// Replaces the per-component pattern:
//   const error   = useSelector((s) => s.ui.error);
//   const success = useSelector((s) => s.ui.success);
//   const info    = useSelector((s) => s.ui.info);
//   useEffect(() => { evaluateError(); }, [error]);
//   useEffect(() => { evaluateSuccess(); }, [success]);
//
// Usage:
//   const { loading, success, error, info } = useAsyncState({
//     onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
//     onSuccess: (info) => { /* component-specific */ },
//   });
//
// Callbacks are optional — if you only need the values, just destructure them.
export default function useAsyncState({ onError, onSuccess } = {}) {
  const loading = useSelector((s) => s.ui.loading);
  const success = useSelector((s) => s.ui.success);
  const error = useSelector((s) => s.ui.error);
  const info = useSelector((s) => s.ui.info);

  // useRef lets the latest callback fire from the effect without re-running
  // the effect every render when the caller passes a fresh function.
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
  }, [onError, onSuccess]);

  useEffect(() => {
    if (error === true && onErrorRef.current) {
      onErrorRef.current(info);
    }
  }, [error, info]);

  useEffect(() => {
    if (success === true && onSuccessRef.current) {
      onSuccessRef.current(info);
    }
  }, [success, info]);

  return { loading, success, error, info };
}
